import {
	estimateDisplayAtom,
	estimateServiceBreakdownAtom,
	offPeakCUSAtom,
	peakCUSAtom,
	pricingSnackbarAtom,
} from '@client/jotai/storefront/pricing';
import { getEstimate } from '@hooks/use-persisted-estimate';
import { useAppSelector } from '@redux/hooks';
import { Snackbar, SnackbarLink } from '@tkww/orion-web-ds-feedback';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { CostBreakdown } from './components/CostBreakdown';
import { MinimumSpendAlert } from './components/MinimumSpendAlert';
import { PriceCard } from './components/PriceCard';
import { SeasonalPricingHeading } from './components/SeasonalPricingHeading';
import Styles from './styles.scss';
import { buildEstimateDisplay } from './utils/estimateDisplay';

export type SeasonType = 'peak' | 'off-peak';

export const SeasonalPricing = () => {
	const peakCUS = useAtomValue(peakCUSAtom);
	const offPeakCUS = useAtomValue(offPeakCUSAtom);
	const estimateDisplay = useAtomValue(estimateDisplayAtom);
	const setEstimateDisplay = useSetAtom(estimateDisplayAtom);
	const setEstimateServiceBreakdown = useSetAtom(estimateServiceBreakdownAtom);
	const [selectedSeason, setSelectedSeason] = useState<SeasonType>('peak');

	const snackbarMessage = useAtomValue(pricingSnackbarAtom);
	const setSnackbarMessage = useSetAtom(pricingSnackbarAtom);

	const snackbarCallbackRef = useCallback(
		(controller: Snackbar.Controller | null) => {
			if (controller && snackbarMessage) {
				controller.showAndDismiss({
					data: snackbarMessage,
					dismissTimeout: 5000,
				});
				setSnackbarMessage(null);
			}
		},
		[snackbarMessage, setSnackbarMessage],
	);

	const storefrontId = useAppSelector(
		(state) => state.rfq.storefrontId || state.vendor.vendorRaw?.id || '',
	);

	useEffect(() => {
		if (!storefrontId) {
			setEstimateDisplay(null);
			setEstimateServiceBreakdown(null);
			return;
		}

		const persisted = getEstimate(storefrontId);

		if (!persisted) {
			setEstimateDisplay(null);
			setEstimateServiceBreakdown(null);
			return;
		}

		setEstimateDisplay(buildEstimateDisplay(persisted));
		setEstimateServiceBreakdown(persisted.serviceBreakdown ?? null);
	}, [storefrontId, setEstimateDisplay, setEstimateServiceBreakdown]);

	const showTopToggle =
		!estimateDisplay && !!peakCUS && !!offPeakCUS && peakCUS !== offPeakCUS;

	const showCostBreakdown = !!estimateDisplay;
	const belowMinimumSpend =
		estimateDisplay?.isBelowMinimumSpend &&
		estimateDisplay.originalEstimate &&
		estimateDisplay.minimumSpend
			? {
					originalEstimate: estimateDisplay.originalEstimate,
					minimumSpend: estimateDisplay.minimumSpend,
				}
			: null;

	return (
		<div
			className={Styles.seasonalPricingContainer}
			data-testid="seasonal-pricing-container"
		>
			<div
				className={Styles.seasonalPricingTop}
				data-testid="seasonal-pricing-top"
			>
				{showTopToggle && (
					<SeasonalPricingHeading
						selectedSeason={selectedSeason}
						onChange={setSelectedSeason}
					/>
				)}
				<PriceCard
					selectedSeason={selectedSeason}
					hasSeasonToggle={showTopToggle}
				/>
			</div>
			{belowMinimumSpend && (
				<MinimumSpendAlert
					originalEstimate={belowMinimumSpend.originalEstimate}
					minimumSpend={belowMinimumSpend.minimumSpend}
				/>
			)}
			{showCostBreakdown && <CostBreakdown />}
			<Snackbar ref={snackbarCallbackRef}>
				{(data: { message: string; conversationUrl?: string }) => (
					<span>
						{data.message}{' '}
						{data.conversationUrl && (
							<SnackbarLink
								href={data.conversationUrl}
								className={Styles.snackbarLink}
							>
								Follow up in your inbox
							</SnackbarLink>
						)}
					</span>
				)}
			</Snackbar>
		</div>
	);
};
